import React, { useCallback, useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { tokens } from "../../../theme";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  Box,
} from "@mui/material";
import {
  deleteType,
  getTypesPaginated,
  getTypesError,
  getTypesStatus,
  selectAllTypes,
  selectTypesPagination,
  selectTypesLastParams,
  selectTypesQuery,
  setTypesQuery,
  resetTypesQuery,
  updateStatus,
  getTypesSuccess,
} from "../../../features/typeSlice";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import AlertDialog from "../../../components/alertDialog";
import { toast } from "react-toastify";
import AppBarSearch from "../../../global/appBarSearch";
import NoDataTable from "../../../components/noDataTable";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import {
  ChoiceTypeList,
  getOptionModeLabel,
  OptionMode,
  OptionModeList,
  PaymentTypeList,
} from "../../../models/option.model";
import FiltersDialog, {
  FiltersButton,
} from "../../../components/filters/filtersDialog";
import FilterChipGroup from "../../../components/filters/filterChipGroup";
import { NameSortList, applySort } from "../../../models/sort.model";
import { ellipsisText } from "../../../global/functions/textFormat";
import ParagraphHighliter from "../../../components/paragraphHighliter";
import { ResponsiveProvider } from "../../../hooks/uiHook";
import ShowMultiItems from "../../../components/showMultiItems";
import { useTranslation } from "react-i18next";
import TableActions from "../../../components/tableActions";
const Option = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { restaurantId } = useRestaurant();
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const query = useSelector(selectTypesQuery);
  const { search, limit } = query;
  const page = query.page - 1;
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("");
  const [choiceTypeFilter, setChoiceTypeFilter] = useState("");
  const [optionModeFilter, setOptionModeFilter] = useState("");
  const [sortFilter, setSortFilter] = useState("");
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  // Draft copies bound to the FiltersDialog controls; committed to the
  // applied state above only when Appliquer is clicked.
  const [draftPaymentTypeFilter, setDraftPaymentTypeFilter] = useState("");
  const [draftChoiceTypeFilter, setDraftChoiceTypeFilter] = useState("");
  const [draftOptionModeFilter, setDraftOptionModeFilter] = useState("");
  const [draftSortFilter, setDraftSortFilter] = useState("");
  const status = useSelector(getTypesStatus);
  const error = useSelector(getTypesError);
  const options = useSelector(selectAllTypes);
  const pagination = useSelector(selectTypesPagination);
  const lastFetchedParams = useSelector(selectTypesLastParams);
  const success = useSelector(getTypesSuccess);
  const totalCount = pagination.totalRecords || 0;

  const handleClickOpen = (cardId) => {
    setOpen(true);
    setCardId(cardId);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("add");
  };

  const handleModify = (data) => {
    navigate("modify", { state: { type: data } });
  };

  // Reset the search/pagination query on every fresh mount (not just on
  // restaurant switch, handled separately below) so navigating away (e.g. to
  // Add/Modify, which unmounts this whole screen) and back always starts
  // from a clean search, consistent with Utilisateurs/Commandes where search
  // is local state that resets in full. Registered above the fetch effect.
  useEffect(() => {
    dispatch(resetTypesQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let effectiveSearch = search;
    let effectiveLimit = limit;
    let effectivePage = page + 1;

    if (lastFetchedParams) {
      let previousRestaurantId;
      try {
        previousRestaurantId = JSON.parse(lastFetchedParams).restaurantId;
      } catch {
        previousRestaurantId = undefined;
      }
      if (previousRestaurantId !== restaurantId) {
        effectiveSearch = "";
        effectiveLimit = 10;
        effectivePage = 1;
        dispatch(resetTypesQuery());
      }
    }

    const params = {
      restaurantId,
      search: effectiveSearch,
      limit: effectiveLimit,
      page: effectivePage,
    };
    const serialized = JSON.stringify(params);
    if (lastFetchedParams === serialized && status === "fetchDataPaginated") {
      return;
    }
    dispatch(getTypesPaginated(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, restaurantId, search, limit, page]);

  // Live draft-vs-applied diff: Appliquer stays disabled until at least one
  // draft filter differs from what's currently applied, and re-disables
  // itself automatically if the user changes a filter back to match.
  const hasPendingChanges =
    draftPaymentTypeFilter !== paymentTypeFilter ||
    draftChoiceTypeFilter !== choiceTypeFilter ||
    draftOptionModeFilter !== optionModeFilter ||
    draftSortFilter !== sortFilter;

  // Payment/choice/option-mode are not (yet) supported as query params by the
  // backend /type endpoint, so they're applied client-side on the current
  // page, then sorted.
  const filteredOptions = applySort(
    (options || []).filter((option) => {
      if (
        paymentTypeFilter !== "" &&
        String(option.payment) !== String(paymentTypeFilter)
      ) {
        return false;
      }
      if (
        choiceTypeFilter !== "" &&
        String(option.selection) !== String(choiceTypeFilter)
      ) {
        return false;
      }
      if (
        optionModeFilter !== "" &&
        String(option.mode) !== String(optionModeFilter)
      ) {
        return false;
      }
      return true;
    }),
    sortFilter
  );

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success);
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      toast.error(error);
    }
  }, [dispatch, error, status, success]);

  const handleChangePage = useCallback(
    (event, newPage) => {
      const maxPage = Math.max(0, Math.ceil(totalCount / limit) - 1);
      const clampedPage = Math.min(newPage, maxPage);
      dispatch(setTypesQuery({ page: clampedPage + 1 }));
    },
    [dispatch, limit, totalCount]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const newLimit = Number(event.target.value);
      dispatch(setTypesQuery({ limit: newLimit, page: 1 }));
    },
    [dispatch]
  );

  return (
    <div className="main-application">
      <CssBaseline />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          disableMobileDrawer
          searchText={t("search.byNameLabelMessage")}
          handleSubmit={handleSubmit}
          handleSearch={(e) => {
            dispatch(setTypesQuery({ search: e.target.value, page: 1 }));
          }}
          title={t("pages.myOptions")}
          buttonTitle={t("actions.addOption")}
          children={
            <>
              <FiltersButton
                onClick={() => {
                  setDraftPaymentTypeFilter(paymentTypeFilter);
                  setDraftChoiceTypeFilter(choiceTypeFilter);
                  setDraftOptionModeFilter(optionModeFilter);
                  setDraftSortFilter(sortFilter);
                  setFiltersDialogOpen(true);
                }}
                activeCount={
                  (paymentTypeFilter !== "" ? 1 : 0) +
                  (choiceTypeFilter !== "" ? 1 : 0) +
                  (optionModeFilter !== "" ? 1 : 0) +
                  (sortFilter ? 1 : 0)
                }
              />
            </>
          }
        />
      </ResponsiveProvider>
      <FiltersDialog
        open={filtersDialogOpen}
        onClose={() => setFiltersDialogOpen(false)}
        onReset={() => {
          setDraftPaymentTypeFilter("");
          setPaymentTypeFilter("");
          setDraftChoiceTypeFilter("");
          setChoiceTypeFilter("");
          setDraftOptionModeFilter("");
          setOptionModeFilter("");
          setDraftSortFilter("");
          setSortFilter("");
          setFiltersDialogOpen(false);
        }}
        onApply={() => {
          setPaymentTypeFilter(draftPaymentTypeFilter);
          setChoiceTypeFilter(draftChoiceTypeFilter);
          setOptionModeFilter(draftOptionModeFilter);
          setSortFilter(draftSortFilter);
          setFiltersDialogOpen(false);
        }}
        disableApply={!hasPendingChanges}
      >
        <FilterChipGroup
          label={t("filters.allPaymentTypes")}
          options={PaymentTypeList}
          value={draftPaymentTypeFilter}
          onChange={setDraftPaymentTypeFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.allChoices")}
          options={ChoiceTypeList}
          value={draftChoiceTypeFilter}
          onChange={setDraftChoiceTypeFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.allOptionTypes")}
          options={OptionModeList}
          value={draftOptionModeFilter}
          onChange={setDraftOptionModeFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.sortBy")}
          options={NameSortList}
          value={draftSortFilter}
          onChange={setDraftSortFilter}
        />
      </FiltersDialog>
      <main>
        <Box sx={{ my: 2 }}>
          <TableContainer>
            <Table
              sx={{
                backgroundColor: colors.primary[400],
              }}
            >
              <TableHead
                sx={{
                  backgroundColor: colors.primary[700],
                }}
              >
                <TableRow>
                  <TableCell>{t("table.name")}</TableCell>
                  <TableCell>{t("table.label")}</TableCell>
                  <TableCell>{t("table.message")}</TableCell>
                  <TableCell>{t("table.min")}</TableCell>
                  <TableCell>{t("table.max")}</TableCell>
                  <TableCell>{t("table.paid")}</TableCell>
                  <TableCell>{t("table.choice")}</TableCell>
                  <TableCell>{t("table.type")}</TableCell>
                  <TableCell>{t("table.productsIngredients")}</TableCell>
                  <TableCell align="right">{t("table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status === "loadingFetchDataPaginated" ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Loading size={8} />
                    </TableCell>
                  </TableRow>
                ) : status === "fetchErrorPaginated" ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Error>{error}</Error>
                    </TableCell>
                  </TableRow>
                ) : filteredOptions && filteredOptions.length > 0 ? (
                  filteredOptions.map((card) => (
                    <TableRow key={card._id}>
                      <TableCell>
                        <ParagraphHighliter title={card.name} search={search} />
                      </TableCell>
                      <TableCell>
                        <ParagraphHighliter
                          title={card.label}
                          search={search}
                        />
                      </TableCell>
                      <TableCell>
                        <ParagraphHighliter
                          title={ellipsisText(card?.message)}
                          search={search}
                        />
                      </TableCell>
                      <TableCell>{card.min}</TableCell>
                      <TableCell>{card.max}</TableCell>
                      <TableCell>{card.payment ? t("common.yes") : t("common.no")}</TableCell>
                      <TableCell>
                        {card.selection ? t("filters.multiple") : t("filters.single")}
                      </TableCell>
                      <TableCell>{getOptionModeLabel(card.mode)}</TableCell>
                      <TableCell>
                        {card.mode === OptionMode.products ||
                        card.mode === OptionMode.ingredients ? (
                          <ShowMultiItems
                            items={
                              card.mode === OptionMode.products
                                ? card.products.map((p) => ({ _id: p._id, name: p.name }))
                                : card.ingredients.map((i) => ({
                                    _id: i._id,
                                    name: i.name,
                                  }))
                            }
                            useStyledTypography={false}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <TableActions
                          actions={[
                            {
                              type: "delete",
                              color: "error",
                              label: t("common.delete"),
                              onClick: () => handleClickOpen(card._id),
                            },
                            {
                              type: "edit",
                              color: "warning",
                              label: t("common.edit"),
                              onClick: () => handleModify(card),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <NoDataTable cols={10} />
                )}
              </TableBody>
            </Table>
            <TablePagination
              sx={{ backgroundColor: colors.primary[700] }}
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={limit}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <AlertDialog
              handleClose={() => setOpen(false)}
              open={open}
              name={"option"}
              cardId={cardId}
              deleteData={() =>
                dispatch(deleteType({ typeId: cardId, restaurantId }))
              }
            />
          </TableContainer>
        </Box>
      </main>
    </div>
  );
};

export default Option;
